import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { ScrollText, User, Upload, Brain, LogIn, Trash2, UserPlus, Database, Search, AlertTriangle, RefreshCw, Settings, Key } from 'lucide-react';
import { toast } from 'sonner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatDisplayDateTime } from '@/utils/dateFormat';

const ACTION_ICONS = {
  user_login: LogIn,
  user_created: UserPlus,
  csv_upload: Upload,
  insight_deleted: Trash2,
  data_seed: Database,
  data_reset_completed: Trash2,
  alert_generation: AlertTriangle,
  password_reset_requested: Key,
  summary_rebuild: RefreshCw,
  config_updated: Settings,
};

const ACTION_COLORS = {
  user_login: 'text-blue-600 dark:text-blue-400',
  user_created: 'text-green-600 dark:text-green-400',
  csv_upload: 'text-amber-600 dark:text-amber-400',
  insight_deleted: 'text-red-600 dark:text-red-400',
  data_seed: 'text-purple-600 dark:text-purple-400',
  data_reset_completed: 'text-red-600 dark:text-red-400',
  alert_generation: 'text-amber-600 dark:text-amber-400',
  password_reset_requested: 'text-orange-600 dark:text-orange-400',
};

const PER_PAGE = 50;

export default function AuditLogPage() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ logs: [], total: 0, actions: [] });
  const [actionFilter, setActionFilter] = useState('');
  const [emailFilter, setEmailFilter] = useState('');
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PER_PAGE, skip: (page - 1) * PER_PAGE };
      if (actionFilter) params.action = actionFilter;
      if (emailFilter) params.user_email = emailFilter;
      const res = await api.get('/audit', { params });
      setData(res.data);
    } catch {
      toast.error('Failed to load audit log');
    } finally {
      setLoading(false);
    }
  }, [api, actionFilter, emailFilter, page]);

  useEffect(() => {
    const t = setTimeout(load, emailFilter ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, emailFilter]);

  const totalPages = Math.ceil((data.total || 0) / PER_PAGE);

  const formatDate = (iso) => formatDisplayDateTime(iso);

  const formatDetails = (details) => {
    if (!details || Object.keys(details).length === 0) return '-';
    return Object.entries(details).map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`).join(', ');
  };

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-heading font-bold tracking-tight">Audit Log</h1>
        <p className="text-sm text-muted-foreground mt-1">Track all admin and system actions</p>
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <Select value={actionFilter} onValueChange={v => { setActionFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-48" data-testid="audit-filter-action"><SelectValue placeholder="All Actions" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Actions</SelectItem>
            {data.actions?.map(a => <SelectItem key={a} value={a}>{a.replace(/_/g, ' ')}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Filter by user/email..." value={emailFilter} onChange={e => { setEmailFilter(e.target.value); setPage(1); }}
            className="pl-8 w-56" data-testid="audit-filter-email" />
        </div>
        <span className="text-xs text-muted-foreground">{data.total?.toLocaleString('en-IN')} entries</span>
        {totalPages > 1 && (
          <div className="flex items-center gap-2 ml-auto">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} className="px-2 py-1 text-xs rounded border border-border disabled:opacity-30 hover:bg-muted" data-testid="audit-prev-page">Prev</button>
            <span className="text-xs text-muted-foreground">{page} / {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="px-2 py-1 text-xs rounded border border-border disabled:opacity-30 hover:bg-muted" data-testid="audit-next-page">Next</button>
          </div>
        )}
      </div>

      <div className="bg-card border border-border rounded-md p-5 overflow-auto">
        {loading ? (
          <p className="text-sm text-muted-foreground text-center py-8">Loading audit log...</p>
        ) : data.logs?.length === 0 ? (
          <div className="text-center py-12">
            <ScrollText size={32} strokeWidth={1} className="mx-auto text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">No audit entries found.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs w-[180px]">Timestamp</TableHead>
                <TableHead className="text-xs w-[140px]">Action</TableHead>
                <TableHead className="text-xs w-[140px]">User</TableHead>
                <TableHead className="text-xs">Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.logs.map((log, i) => {
                const Icon = ACTION_ICONS[log.action] || ScrollText;
                const color = ACTION_COLORS[log.action] || 'text-muted-foreground';
                return (
                  <TableRow key={log.id || i} data-testid={`audit-row-${i}`}>
                    <TableCell className="text-xs text-muted-foreground">{formatDate(log.timestamp)}</TableCell>
                    <TableCell className="text-sm">
                      <span className={`flex items-center gap-1.5 ${color}`}>
                        <Icon size={13} strokeWidth={1.5} />
                        <span className="capitalize">{log.action?.replace(/_/g, ' ')}</span>
                      </span>
                    </TableCell>
                    <TableCell className="text-sm">{log.user_name || '-'}</TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[300px] truncate">{formatDetails(log.details)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Bottom pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} className="px-3 py-1.5 text-xs rounded border border-border disabled:opacity-30 hover:bg-muted">Previous</button>
          <span className="px-3 py-1.5 text-xs text-muted-foreground">Page {page} of {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="px-3 py-1.5 text-xs rounded border border-border disabled:opacity-30 hover:bg-muted">Next</button>
        </div>
      )}
    </div>
  );
}
